import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type {
	BoardPreviewMap,
	CreateRecordProperties,
	PopupListItem,
	RecordData,
	Service,
} from 'webapp-types';
import { ModelName, UserAccess } from 'webapp/enums/enums';
import { getSourcesTexts, uniq, uniqBy } from 'webapp/utils/common';
import { getFormattedTime } from 'webapp/utils/number';

import type Board from 'webapp/models-custom/board';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type Template from 'webapp/models-custom/template';
import type BoardLoopRecord from 'webapp/models/board-loop';
import type TemplateRecord from 'webapp/models/template';
import type UserRecord from 'webapp/models/user';

import { Severity } from 'webapp/services/logger';
import type { SourceListEntry } from 'webapp/services/model-manager/sources';

export type Action =
	| 'openPreview'
	| 'openPublicTemplateEditPopup'
	| 'edit'
	| 'toggleFavorite'
	| 'createNotification'
	| 'share'
	| 'openFullscreenView'
	| 'openInDesigner'
	| 'duplicate'
	| 'createReport'
	| 'addToTeam'
	| 'openActivityLog'
	| 'deleteLoop'
	| 'saveAsTemplate'
	| 'restore'
	| 'deletePermanently'
	| 'openChangeOwnerPopup';
export type Option = PopupListItem<Action>;

export default class BoardLoop extends BufferedModel<
	BoardLoopRecord,
	RecordData.BoardLoop.Serialized
> {
	@service('account') declare accountService: Service.Account;
	@service('logger') declare loggerService: Service.Logger;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get title(): string {
		return this.getBuffered('title');
	}

	set title(value: string) {
		this.setBuffered('title', value);
	}

	get ownerId(): number | null {
		return this.getBuffered('ownerId');
	}

	set ownerId(value: number | null) {
		this.setBuffered('ownerId', value);
	}

	get boardIds(): number[] {
		return this.getBuffered('boardIds');
	}

	set boardIds(value: number[]) {
		this.setBuffered('boardIds', value);
	}

	get token(): string {
		return this.getBuffered('token');
	}

	set token(value: string) {
		this.setBuffered('token', value);
	}

	get isPublicEnabled(): boolean {
		return this.getBuffered('isPublicEnabled');
	}

	set isPublicEnabled(value: boolean) {
		this.setBuffered('isPublicEnabled', value);
	}

	get created(): number {
		return this.model.created;
	}

	get createdBy(): number {
		return this.model.createdBy;
	}

	get tags(): number[] {
		return this.getBuffered('tags');
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get loopDelay(): number {
		return this.getBuffered('loopDelay');
	}

	set loopDelay(value: number) {
		this.setBuffered('loopDelay', value);
	}

	get restrictIps(): string[] {
		return this.getBuffered('restrictIps');
	}

	set restrictIps(value: string[]) {
		this.setBuffered('restrictIps', value);
	}

	get password(): string {
		return this.getBuffered('password');
	}

	set password(value: string) {
		this.setBuffered('password', value);
	}

	get allowAnonymousDateRangeEdit(): boolean {
		return this.getBuffered('allowAnonymousDateRangeEdit');
	}

	set allowAnonymousDateRangeEdit(value: boolean) {
		this.setBuffered('allowAnonymousDateRangeEdit', value);
	}

	get allowAnonymousFilterEdit(): boolean {
		return this.getBuffered('allowAnonymousFilterEdit');
	}

	set allowAnonymousFilterEdit(value: boolean) {
		this.setBuffered('allowAnonymousFilterEdit', value);
	}

	get favorite(): boolean {
		return this.getBuffered('favorite');
	}

	set favorite(value: boolean) {
		this.setBuffered('favorite', value);
	}

	get users(): number[] {
		return this.getBuffered('users');
	}

	set users(value: number[]) {
		this.setBuffered('users', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get access(): number {
		return this.getBuffered('access');
	}

	set access(value: number) {
		this.setBuffered('access', value);
	}

	get templateId(): number | null {
		return this.getBuffered('templateId');
	}

	set templateId(value: number | null) {
		this.setBuffered('templateId', value);
	}

	get originateFrom(): number | null {
		return this.getBuffered('originateFrom');
	}

	set originateFrom(value: number | null) {
		this.setBuffered('originateFrom', value);
	}

	get spaceId(): number {
		return this.model.spaceId;
	}

	get deleted(): number | null {
		return this.getBuffered('deleted');
	}

	set deleted(value: number | null) {
		this.setBuffered('deleted', value);
	}

	// ----------- Template stuff -----------

	get templateRecord(): TemplateRecord | null {
		const templateId = this.templateId;
		let record;
		if (templateId) {
			record = this.mmTemplatesService.findRecord(String(templateId));
		}
		return record || null;
	}

	_templateModel: Template | null = null;

	@cached
	get templateModel(): Template | null {
		const model = this.templateRecord;

		if (model && model?.id !== this._templateModel?.id) {
			this._templateModel = this.mmTemplatesService.createModel(model);
		}

		return this._templateModel;
	}

	get isTemplate(): boolean {
		return !!this.templateRecord;
	}

	get isPublicTemplate(): boolean {
		return !!this.templateRecord?.isPublic;
	}

	get isAccountTemplate(): boolean {
		return this.isTemplate && !this.templateRecord?.isPublic;
	}

	// ------- other props ------

	get createdById() {
		return this.createdBy; // for filter-sort/mixed-in-designer
	}

	get isForCurrentSpace(): boolean {
		return !this.spaceId || this.spaceId === this.accountService.id;
	}

	get name(): string {
		return this.title; // to be compatible with snapshots listing
	}

	get isMine(): boolean {
		return this.createdBy === this.currentUserService.numericId;
	}

	get isOwnedByMe(): boolean {
		return this.ownerId === this.currentUserService.numericId;
	}

	get canCurrentUserEditWithoutDeleted(): boolean {
		// is creator or isnt permission level user
		return (
			(!this.isPublicTemplate || this.accountService.isPublictemplatesSpace) &&
			!this.currentUserService.model?.isViewer &&
			(this.isMine || !this.currentUserService.model?.isUser) &&
			this.isForCurrentSpace
		);
	}

	get canCurrentUserEdit(): boolean {
		// is creator or isnt permission level user
		return this.canCurrentUserEditWithoutDeleted && !this.deleted;
	}

	get canCurrentUserEditSomeBoard(): boolean {
		return this.canCurrentUserEdit && !!this.boardsEditableByUser.length;
	}

	get canEditPermissions(): boolean {
		const { model, isAdmin } = this.currentUserService;

		return (this.isMine || isAdmin) && !!model && !model.isViewer;
	}

	get favoriteText(): string {
		return this.favorite ? 'Unfavorite' : 'Favorite';
	}

	get createdText(): string {
		return getFormattedTime(this.created, this.currentUserService.model?.timezone);
	}

	get createdByUser(): UserRecord | null {
		let user;
		if (this.createdBy) {
			user = this.mmUsersService.findRecord(String(this.createdBy));
			if (!user) {
				this.loggerService.logError(
					'Missing createdBy for board loop',
					{
						loopId: this.id,
						userId: this.createdBy,
					},
					Severity.WARNING,
				);
			}
		}
		return user || null;
	}

	get ownerUser(): UserRecord | null {
		let user;
		if (this.ownerId) {
			user = this.mmUsersService.findRecord(String(this.ownerId));
			// if (!user) {
			// 	this.loggerService.logError('Missing owner for board loop', {
			// 		loopId: this.id,
			// 		userId: this.ownerId
			// 	}, Severity.WARNING);
			// }
		}
		return user || null;
	}

	get internalUrl(): string {
		return this.accountService.appUrl + '/databoards/loops/' + this.id;
	}

	get datawallUrl(): string {
		return this.accountService.appUrl + '/datawall/' + this.token;
	}

	@cached
	get tagsObjs() {
		return this.mmTagsService.savedRecords.filter((tag) => {
			return this.tags.includes(+tag.id!);
		});
	}

	get tagsText(): string {
		return this.tagsObjs.map((item) => item.name).join(', ') || '';
	}

	get isPrivate(): boolean {
		return this.access === UserAccess.PRIVATE;
	}

	get boards(): Board[] {
		const boards: Board[] = [];
		this.boardIds.forEach((boardId: number) => {
			const board = this.mmBoardsService.boardModels.find(
				(item) => item.id === String(boardId),
			);
			if (board) {
				boards.push(board);
			}
		});
		return boards;
	}

	get boardsEditableByUser(): Board[] {
		return this.boards.filter((board: Board) => {
			return board.canCurrentUserEdit;
		});
	}

	get boardsTooltipText(): string {
		return this.boards.map((item) => item.name).join('<br />');
	}

	get boardCount(): number {
		return this.boards.length;
	}

	get boardCountText(): string {
		const boardCount = this.boardCount;

		return boardCount ? `${boardCount} dashboard${boardCount > 1 ? 's' : ''}` : '';
	}

	get boardNamesText(): string {
		return this.boards.map((item) => item.name).join(', ');
	}

	get image(): string {
		return this.boards[0]?.image || '';
	}

	get color(): string {
		return this.boards[0]?.color || '';
	}

	get bgTheme(): string {
		return this.boards[0]?.bgTheme || '';
	}

	get imageMediumOrDefault(): string {
		return this.boards[0]?.imageMediumOrDefault || '';
	}

	get isAutoLoop(): boolean {
		return this.loopDelay > 0;
	}

	@cached
	get specificSourceTypesKeys(): string[] {
		const specificSourceTypesKeys: string[] = [];

		this.boards.forEach((board) => {
			specificSourceTypesKeys.push(...board.specificSourceTypesKeys);
		});

		return uniq(specificSourceTypesKeys);
	}

	get sourcesList() {
		const sources: SourceListEntry[] = [];
		this.boards.forEach((board) => {
			board.sourcesList.forEach((sourceInfo) => {
				sources.push(sourceInfo);
			});
		});
		return uniqBy(sources, 'value');
	}

	@cached
	get firstSourcesText(): string {
		return getSourcesTexts(this.sourcesList).first;
	}

	@cached
	get lastSourcesText(): string {
		return getSourcesTexts(this.sourcesList).last;
	}

	@cached
	get sourcesOtherText(): string {
		return getSourcesTexts(this.sourcesList).other;
	}

	@cached
	get sourcesText(): string {
		const sourcesTexts = getSourcesTexts(this.sourcesList);
		return sourcesTexts.first + ' ' + sourcesTexts.other;
	}

	get isFull(): boolean {
		return this.boardIds.length >= BoardLoop.maxBoardsInLoop;
	}

	get tagsCount(): number {
		return this.tags.length;
	}

	get boardSourceCount(): number {
		return this.sourcesList.length;
	}

	get allImages() {
		const images: string[] = [];
		this.boards.forEach((board) => {
			images.push(...board.allImages);
		});
		return uniq(images);
	}

	get isDemoAccount(): boolean {
		return this.accountService.isDemoAccount;
	}

	getAdditionalOptions(
		isLoopsPage = true,
		isPreviewPopup?: boolean,
		isTeamSectionItem = false,
	): Option[] {
		const options: Option[] = [];

		if (this.deleted) {
			if (this.canCurrentUserEditWithoutDeleted) {
				options.push(
					{
						text: 'Restore',
						action: 'restore',
						icon: 'Restore',
					},
					{
						class: 'delete-permanently',
						text: 'Delete permanently',
						action: 'deletePermanently',
						icon: 'Trash',
					},
				);
			}
			return options;
		}

		if (isLoopsPage) {
			options.push({
				text: 'View',
				action: 'openPreview',
				icon: 'Databoards',
			});
		}

		if (this.accountService.isPublictemplatesSpace) {
			options.push({
				text: this.isTemplate ? 'Edit public template' : 'Save as public template',
				action: 'openPublicTemplateEditPopup',
				icon: 'FolderOpen',
			});
		}

		if (this.canCurrentUserEdit) {
			const isLoopsOrPreviewPopup = isLoopsPage || isPreviewPopup;

			if (!isPreviewPopup) {
				options.push({
					text: 'Edit',
					action: 'edit',
					icon: 'Pencil',
				});
			}

			if (isLoopsOrPreviewPopup) {
				options.push({
					class: this.model.favorite ? 'accent' : '',
					text: this.favoriteText,
					action: 'toggleFavorite',
					icon: this.model.favorite ? 'StarFill' : 'Star',
				});
			}

			if (isLoopsPage) {
				options.push({
					icon: 'Share',
					text: 'Share',
					action: 'share',
				});

				const notificationsOption: PopupListItem<Action> | null =
					this.mmNotificationsService.getNotificationsDropdownOptionForFeature(
						this,
						'createNotification',
					);
				if (notificationsOption) {
					options.push(notificationsOption);
				}
			}

			if (!isPreviewPopup) {
				options.push({
					icon: 'FullScreen',
					text: 'Full-screen view',
					action: 'openFullscreenView',
				});
			}

			if (isLoopsPage && this.canCurrentUserEditSomeBoard) {
				options.push({
					icon: 'PencilRuler',
					text: 'Open in Designer',
					action: 'openInDesigner',
				});
			}

			if (isLoopsOrPreviewPopup) {
				options.push({
					icon: 'FolderUpload',
					text: 'Save as template',
					action: 'saveAsTemplate',
					isDisabled: this.isPublicTemplate,
					extra: this.billingService.accountTemplatesEnabled
						? undefined
						: {
								type: 'featureLimitation',
							},
				});
			}

			if (!isTeamSectionItem) {
				options.push({
					text: 'Duplicate',
					action: 'duplicate',
					icon: 'Copy',
				});
			}

			if (isLoopsOrPreviewPopup) {
				options.push({
					icon: 'Reports',
					text: 'New report',
					action: 'createReport',
				});
			}

			if (!isTeamSectionItem) {
				options.push({
					icon: 'Spaces',
					text: 'Add to Space',
					action: 'addToTeam',
				});
			}

			if (this.currentUserService.isAdmin) {
				options.push(this.mmActivityLogsService.getObjectForOptionsPopup<Action>());
			}

			if (!this.isDemoAccount) {
				options.push({
					icon: 'User',
					text: 'Change owner',
					action: 'openChangeOwnerPopup',
				});
			}

			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'deleteLoop',
				icon: 'Trash',
			});
		}

		return options;
	}

	changeAccess(newAccess: number, usersPermissionIds: number[] = []): boolean {
		if (this.accountService.handleDemo()) {
			return true;
		}

		let selectedUsers: number[] = [];

		switch (newAccess) {
			case UserAccess.PRIVATE:
				if (!this.isMine) {
					this.infolayerService.error(
						'You can set to private only looped dashboards you have created!',
					);
					return false;
				}
				break;
			case UserAccess.RESTRICTED:
				selectedUsers = usersPermissionIds;
				break;
			case UserAccess.PUBLIC:
				break;
			default:
				this.loggerService.logError('Unrecognized Looped Databoard access!', {
					boardLoopId: this.id,
					boardAccess: newAccess,
				});
				return false;
		}

		this.access = newAccess;

		this.users = selectedUsers;

		return true;
	}

	openPublicTemplateEditPopup(): void {
		this.mmBoardLoopsService.openPublicTemplateEditPopup(this);
	}

	isBoardIncluded(board: Board): boolean {
		return this.boardIds.includes(board.numericId!);
	}

	getDatawallUrlWithPreviewHash(previewHash: BoardPreviewMap = {}, isIframe = false): string {
		return this.mmBoardsService.getUrlWithPreviewHash(this.datawallUrl, previewHash, isIframe);
	}

	getInternalUrlWithPreviewHash(previewHash: BoardPreviewMap = {}, isIframe = false): string {
		return this.mmBoardsService.getUrlWithPreviewHash(this.internalUrl, previewHash, isIframe);
	}

	override getDataForCopy(): CreateRecordProperties<BoardLoopRecord> {
		const data = this.toJSON();
		data.title = `Copy of ${data.title}`;
		delete data.ownerId;
		delete data.token;
		delete data.created;
		delete data.createdBy;
		return data;
	}

	override delete(skipBE = false): Promise<unknown> {
		return super.delete(skipBE).then(() => {
			if (!this.deleted) {
				this.flowPromotionsService.startRecentlyDeletedTourForBoardLoops();
			}
		});
	}

	openChangeOwnerPopup(): void {
		this.mmUsersService.openUserSelectPopup({
			title: 'Looped dashboard owner',
			text: 'Select an owner for looped dashboard:',
			currentUserId: String(this.model.ownerId),
			onSelect: (userId: string | null) => {
				this.model.ownerId = userId ? +userId : null;
				this.model
					.save()
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			},
		});
	}

	// ----------- Static -----------

	static maxBoardsInLoop = 20;

	static override modelName = ModelName.BOARD_LOOP; // store record type
}
